import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users, Database, Package, Activity, BarChart3, Mail, Inbox,
  HardDrive, ClipboardList, FileSpreadsheet, Settings as SettingsIcon,
  ChevronRight, Trash2,
} from 'lucide-react';

// Admin Settings landing page. Renders a grid of tile-cards that point at each
// specific admin sub-area already wired in the sidebar. Replaces a broken
// `/settings` link that used to fall through to /dashboard.
const SECTIONS = [
  {
    title: 'Users & Access',
    items: [
      { name: 'Users', desc: 'Add, modify, deactivate, delete users', href: '/users', icon: Users },
      { name: 'Activity Logs', desc: 'Page-by-page user activity', href: '/admin/activity-logs', icon: Activity },
      { name: 'User Analytics', desc: 'Usage trends per role', href: '/admin/user-analytics', icon: BarChart3 },
      { name: 'Audit Logs', desc: 'CRUD trail for every record', href: '/audit-logs', icon: ClipboardList },
    ],
  },
  {
    title: 'Data',
    items: [
      { name: 'Master Data', desc: 'Currencies, taxes, units, etc.', href: '/master-data', icon: Database },
      { name: 'Material Master', desc: 'Raw-material catalog & rates', href: '/admin/material-master', icon: Package },
      { name: 'Data Exports', desc: 'Bulk export costings & quotations', href: '/exports', icon: FileSpreadsheet },
      { name: 'Data Backups', desc: 'Schedule & download DB backups', href: '/admin/data-backups', icon: HardDrive },
      { name: 'Recently Deleted', desc: 'Restore bulk-deleted records', href: '/admin/recycle-bin', icon: Trash2 },
    ],
  },
  {
    title: 'Email',
    items: [
      { name: 'Email Settings', desc: 'SMTP, ingestion accounts', href: '/admin/email-settings', icon: Mail },
      { name: 'Email Inbox', desc: 'Staged inquiries from inbox', href: '/admin/email-inbox', icon: Inbox },
    ],
  },
];

export default function SettingsPage() {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading uppercase flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-indigo-600" /> Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as <strong>{user?.name}</strong> ({user?.role}). Jump to any admin area below.
        </p>
      </div>

      {SECTIONS.map((section) => (
        <Card key={section.title} className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-base font-heading uppercase">{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`settings-link-${item.href.replace(/\//g, '-')}`}
                  className="group flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-indigo-400 hover:shadow-sm transition-all"
                >
                  <item.icon className="h-5 w-5 mt-0.5 text-indigo-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-indigo-500 transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
